import { useState, useRef, useEffect, useCallback } from "react";
import { API, useTemplates } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import VoiceMicButton from "@/components/VoiceMicButton";
import SavedPromptsPicker from "@/components/SavedPromptsPicker";
import { useSavedPrompts } from "@/hooks/useSavedPrompts";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Link, useNavigate } from "react-router-dom";
import {
  Download, Loader2, Upload, X, Image, Eye, Paperclip, Clock,
  Trash2, ChevronLeft, ChevronRight, FileText, Layers, Lock, ExternalLink, Info, AlertCircle,
  Pencil, Sparkles, Check, Minus, Plus
} from "lucide-react";
import CoachmarkTour from "@/components/CoachmarkTour";
import FirstDeckCelebration from "@/components/FirstDeckCelebration";

const MAX_STYLE_IMAGES = 1;
const MAX_STYLE_BYTES = 3 * 1024 * 1024;  // 3 MB across all plans — see uploadStyleFiles below.

export default function PPTGeneratorPage() {
  const { user, updatePreferences } = useAuth();
  const { templates, loading: templatesLoading } = useTemplates();
  const navigate = useNavigate();

  // ═══ USER LIMITS (from backend per-user config) ═══
  const isAdmin = user?.role === "admin";
  const accountType = user?.account_type || "paid";
  const maxDeckSlides = user?.max_deck_slides || 25;
  const maxContentUploads = user?.max_content_uploads || 10;
  const allowedFileTypes = (user?.allowed_file_types && user.allowed_file_types.length > 0)
    ? user.allowed_file_types
    : [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".png", ".jpg", ".jpeg", ".webp"];
  const isTrial = accountType === "trial";
  // Admins see all templates; regular users see only those in template_access (empty = all for back-compat)
  const visibleTemplates = (isAdmin || !user?.template_access || user.template_access.length === 0)
    ? templates
    : templates.filter(t => user.template_access.includes(t.id));

  // Refs for the two voice-enabled textareas — VoiceMicButton uses these
  // to insert the transcript at the user's cursor position rather than
  // clobbering existing text.
  const contentTextRef = useRef(null);

  // Saved prompts library — shared across the picker components on
  // this page, the RegenerateSlidesModal, and the Personalize tab.
  // We pull the list once and pass it down so the dropdown doesn't
  // refetch on every render.
  const { prompts: savedPrompts, refresh: refreshPrompts } = useSavedPrompts();

  // ═══ SHARED STATE ═══
  // Approach: starts unselected — the user MUST pick one before generation.
  // Was previously "design" as a default but design researchers reported
  // users overlooking the toggle. Forcing a deliberate choice surfaces the
  // tradeoff between Research+Design and Design-only upfront.
  const [approach, setApproach] = useState(""); // "" | "design" | "research"
  const [title, setTitle] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [inspirationFiles, setInspirationFiles] = useState([]);
  const [isDraggingStyle, setIsDraggingStyle] = useState(false);
  const inspirationFileRef = useRef(null);

  // Content input
  const [contentText, setContentText] = useState("");
  const [attachedFiles, setAttachedFiles] = useState([]);
  const fileInputRef = useRef(null);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [genStatus, setGenStatus] = useState("");
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef(null);
  const pollingRef = useRef(null);

  // Single slide result
  const [result, setResult] = useState(null);

  // Multi-slide state
  const [topic, setTopic] = useState("");
  // ═══ UNIFIED MODE: slider drives single-vs-multi ═══
  //
  // We've collapsed the old "Single Slide / Multi-slide Deck" toggle
  // into a single 1–20 slider. `mode` is now a *derived* value — `"single"`
  // when slides === 1, `"multi"` otherwise. The two backend endpoints
  // (`/api/generate` and `/api/generate-storyline`) are unchanged, but
  // there's only one form now; the Style Reference panel animates in
  // when slides === 1 (per product spec).
  //
  // `title` and `topic` are kept as separate state internally because
  // the original payloads use different field names (`title` vs `topic`),
  // but the UI surfaces them as one textbox. We mirror values across
  // via `setUnifiedTitle` so legacy handlers need no rewrite.
  const [numSlides, setNumSlides] = useState(() => {
    const pref = user?.preferences?.default_ppt_mode;
    return pref === "multi" ? "5" : "1";
  });
  const slidesInt = Math.max(1, Math.min(20, parseInt(numSlides, 10) || 1));
  const mode = slidesInt === 1 ? "single" : "multi";
  // Unified title setter — writes both `title` (single-slide path) and
  // `topic` (multi-deck path) so handleGenerateSingle / handleGenerateDeck
  // both read the same value regardless of the current slider position.
  const setUnifiedTitle = (v) => { setTitle(v); setTopic(v); };
  const unifiedTitle = title || topic;
  // Persist the preferred mode as soon as the slider crosses the 1↔>1
  // boundary so the next session opens at the same default.
  useEffect(() => {
    const persisted = user?.preferences?.default_ppt_mode;
    if (persisted !== mode) {
      updatePreferences?.({ default_ppt_mode: mode })?.catch?.(() => {
        /* fire-and-forget — never block UX on a preference save */
      });
    }
  }, [mode]);

  const [deck, setDeck] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const deckPollingRef = useRef(null);

  // Inline upload-rejection error for the dropzone
  const [uploadError, setUploadError] = useState("");
  // Active uploads — used to render a subtle inline loader on the dropzone.
  // We track *names* (not counts) so the placeholder pills below the
  // dropzone show "Uploading: my-doc.pdf · 8.4 MB" rather than just a spinner.
  const [pendingUploads, setPendingUploads] = useState([]);

  // Research-preview state — when in Research mode, after the first call we
  // show the structured research output for the user to review/edit BEFORE
  // running slide generation. This is the P2 "editable interim preview".
  const [researchPreview, setResearchPreview] = useState("");
  const [researchSources, setResearchSources] = useState([]);
  const [researchText, setResearchText] = useState("");  // Raw research prose (for persistence on the slide doc)
  const [showResearchPreview, setShowResearchPreview] = useState(false);
  const [researching, setResearching] = useState(false);
  // Research tier: "public" → Wokelo Pro (free), "premium" → Wokelo Deep
  // research (+1 credit add-on). Sent in the request payload as
  // `research_model` and routed by the backend dispatcher.
  const [researchModel, setResearchModel] = useState("public");
  // One-shot "kicked off in background" dialog. Pops on click, auto-dismisses
  // in 3s while clearing the form so the user can immediately start the next
  // generation. The deck continues processing in the backend and shows up in
  // History when complete.
  const [genStartedOpen, setGenStartedOpen] = useState(false);
  // ETA (seconds) shown inside the kick-off dialog. Pulled from
  // /api/generation-eta — backend tries the user's median wall-clock for
  // similar past jobs first, then falls back to a heuristic. Null until the
  // fetch lands so we don't flash a wrong number on slow connections.
  const [genEtaSeconds, setGenEtaSeconds] = useState(null);

  // Sparseness warning dialog — fires only when title/content look thin
  const [sparseDialog, setSparseDialog] = useState({ open: false, warnings: [], onProceed: null, kind: null, meta: null });

  // Onboarding: snapshot whether the user was in the `pending` coachmark
  // state when generation kicked off — only fire FirstDeckCelebration if
  // this was their first deck/slide. We fetch on mount (silent) and cache
  // in a ref so the success poll branches can fire the event + flip the
  // backend status without an extra round-trip on the success path.
  const onboardingPendingRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/me/onboarding-status`, {
          withCredentials: true,
          // Best-effort enrichment on first visit; never alarm the user.
          _suppressToast: true,
        });
        if (!cancelled && res.data?.needs_onboarding) {
          onboardingPendingRef.current = true;
        }
      } catch {
        // Silent — celebration is a bonus, never block the page.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Helper: fire the celebration modal + mark onboarding complete on the
  // backend. Idempotent — only runs once (resets the ref) and silently
  // ignores errors so a slow /onboarding-complete never blocks UX.
  const fireFirstGenCelebrationIfPending = useCallback((kind, id) => {
    if (!onboardingPendingRef.current) return;
    onboardingPendingRef.current = false;
    window.dispatchEvent(new CustomEvent("onboarding:first-complete", {
      detail: { kind, id },
    }));
    axios.post(`${API}/me/onboarding-complete`, {}, { withCredentials: true })
      .catch(() => { /* silent */ });
  }, []);

  // ═══ INIT ═══
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
      if (deckPollingRef.current) clearInterval(deckPollingRef.current);
    };
  }, []);

  // (Removed) First-visit trial welcome toast — the Upgrade Plan CTA in the
  // sidebar already surfaces the user's available credits, so the toast was
  // redundant noise on first landing.


  useEffect(() => {
    if (templates.length > 0 && !selectedTemplateId) {
      // Prefer default, fall back to first visible (in case default is restricted)
      const def = visibleTemplates.find(t => t.is_default) || visibleTemplates[0];
      if (def) setSelectedTemplateId(def.id);
    } else if (selectedTemplateId && visibleTemplates.length > 0
               && !visibleTemplates.find(t => t.id === selectedTemplateId)) {
      // Currently selected template is no longer visible to this user → reset
      const def = visibleTemplates.find(t => t.is_default) || visibleTemplates[0];
      setSelectedTemplateId(def?.id || "");
    }
  }, [templates, selectedTemplateId, visibleTemplates]);

  // Clamp numSlides to account-level cap
  useEffect(() => {
    const n = parseInt(numSlides);
    if (n > maxDeckSlides) {
      setNumSlides(String(maxDeckSlides));
    }
  }, [maxDeckSlides, numSlides]);

  // ═══ FILE HANDLERS ═══
  const uploadFile = async (file, fileType = "raw_data") => {
    // Track this upload in the pending-uploads list so the dropzone can
    // render a small "Uploading…" pill until the request resolves. Large
    // files (10 MB+ on slow uplinks) can take 10–30s and the user would
    // otherwise stare at an unchanged UI wondering if their click worked.
    const pendingKey = `${file.name}-${file.size}-${Date.now()}`;
    setPendingUploads(prev => [...prev, { key: pendingKey, name: file.name, size: file.size, kind: fileType }]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("file_type", fileType);
      const res = await axios.post(`${API}/upload-file`, fd, { withCredentials: true });
      return { ...res.data, name: file.name };
    } finally {
      setPendingUploads(prev => prev.filter(p => p.key !== pendingKey));
    }
  };

  const handleFileAttach = async (e) => {
    setUploadError("");
    const files = e.target.files;
    if (!files?.length) return;
    const remaining = maxContentUploads - attachedFiles.length;
    if (remaining <= 0) {
      setUploadError(`Max ${maxContentUploads} file(s) allowed on your ${accountType} plan`);
      return;
    }
    const picked = Array.from(files).slice(0, remaining);
    // Enforce allowed file types
    const rejected = picked.filter(f => {
      const ext = ("." + (f.name.split(".").pop() || "")).toLowerCase();
      return !allowedFileTypes.includes(ext);
    });
    if (rejected.length > 0) {
      setUploadError(`${rejected[0].name}: file type not allowed on your ${accountType} plan. Allowed: ${allowedFileTypes.join(", ")}`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    // Trial-specific client-side size check (server also enforces; this is just nicer UX)
    if (isTrial) {
      const tooBig = picked.find(f => f.size > 10 * 1024 * 1024);
      if (tooBig) {
        setUploadError(`${tooBig.name} is ${(tooBig.size/1024/1024).toFixed(1)} MB — Trial plan allows up to 10 MB per file.`);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
    }
    for (const file of picked) {
      try {
        const doc = await uploadFile(file, "raw_data");
        setAttachedFiles(prev => [...prev, doc]);
      } catch (err) {
        // Surface the server rejection reason inline on the dropzone (not a toast)
        const reason = err.response?.data?.detail || `Failed to upload ${file.name}`;
        setUploadError(reason);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleStyleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    await uploadStyleFiles(files);
    if (inspirationFileRef.current) inspirationFileRef.current.value = "";
  };

  const uploadStyleFiles = async (files) => {
    const remaining = MAX_STYLE_IMAGES - inspirationFiles.length;
    if (remaining <= 0) { toast.error(`Max ${MAX_STYLE_IMAGES} style image`); return; }
    // Style references are visual only — restrict to .jpg/.png so we never
    // hand a HEIC/SVG/GIF off to the image-conditioning pipeline (which
    // expects standard raster formats). Cap at 3 MB so we don't blow past
    // Claude's image-input size limit (~5 MB after base64 — well under
    // their 10 MB hard cap once the rest of the prompt is in the request).
    const ALLOWED = new Set(["image/jpeg", "image/png"]);
    for (let i = 0; i < Math.min(files.length, remaining); i++) {
      const f = files[i];
      if (!ALLOWED.has(f.type)) {
        toast.error(`${f.name}: only .jpg or .png allowed for style reference`);
        continue;
      }
      if (f.size > MAX_STYLE_BYTES) {
        toast.error(`${f.name}: style reference must be under 3 MB`);
        continue;
      }
      try {
        const doc = await uploadFile(f, "inspiration");
        setInspirationFiles(prev => [...prev, doc]);
      } catch { toast.error(`Failed: ${f.name}`); }
    }
  };

  const handleStyleDrop = async (e) => {
    e.preventDefault();
    setIsDraggingStyle(false);
    if (e.dataTransfer.files?.length) await uploadStyleFiles(e.dataTransfer.files);
  };

  // ═══ SINGLE SLIDE GENERATION ═══
  const pollSingle = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API}/presentations/${id}`);
      const p = res.data;
      setGenStatus(p.status_detail || "Processing...");
      if (p.status === "complete") {
        clearInterval(pollingRef.current); clearInterval(timerRef.current);
        pollingRef.current = null; timerRef.current = null;
        setResult(p); setGenerating(false);
        toast.success("Slide generated!");
        fireFirstGenCelebrationIfPending("slide", p.id || id);
      } else if (p.status === "failed") {
        clearInterval(pollingRef.current); clearInterval(timerRef.current);
        pollingRef.current = null; timerRef.current = null;
        setGenerating(false);
        // Friendly: never show "failed" — frame as queued so the user is
        // nudged to retry without alarm.
        toast.info("Slide is queued due to heavy traffic — tap History to retry");
      }
    } catch {}
  }, []);

  // ─── Sparseness Validation ─────────────────────────────────────────────
  // Returns an array of human-readable warnings if the inputs look too thin
  // for a useful generation. Empty array = sufficient detail, no popup needed.
  //
  // Validation matrix (per product spec):
  //   1) Single + design only       → content for 1 slide  OR  ≥1 file (content optional)
  //   2) Single + research+design   → 1–2 lines of content (research direction); files optional
  //   3) Multi  + design only       → content for N slides OR  (≥1 file AND a short outline/ToC)
  //   4) Multi  + research+design   → 1–2 lines of guidance (sub-topics, ToC); files optional
  //
  // Returns: { warnings: string[], kind: "thin-design-only" | "general" | null }
  // The `kind` field drives the dialog UX: "thin-design-only" gets the
  // "Add your content / Select Research + Design" two-CTA treatment,
  // everything else uses the generic Edit-details / Generate-anyway dialog.
  const checkSparseness = (titleVal, contentVal, currentMode, currentApproach, fileCount, slidesCount) => {
    const warnings = [];
    const t = (titleVal || "").trim();
    const c = (contentVal || "").trim();
    const titleWords = t ? t.split(/\s+/).length : 0;
    const contentWords = c ? c.split(/\s+/).length : 0;
    const hasFile = (fileCount || 0) > 0;
    const N = parseInt(slidesCount) || 5;
    const isResearch = currentApproach === "research";

    // Title check is universal — vague titles produce vague decks regardless of mode.
    if (t.length < 8 || titleWords < 2) {
      warnings.push({
        title: currentMode === "multi" ? "Topic is too short" : "Title is too short",
        hint: "Add company, time period, or angle.",
        example: "Q4 2026 cloud market analysis",
      });
    }

    // ── Threshold helpers ──────────────────────────────────────────────
    // Light "guidance" pass: ~1-2 lines / ToC-level outline
    const passGuidance = c.length >= 25 && contentWords >= 4;
    // Single-slide full content
    const passSingleContent = c.length >= 40 && contentWords >= 7;
    // Multi-slide full content (scales with N — we don't gate proportionally
    // because the cost grows fast; soft floors are friendlier than hard ones)
    const passMultiContent = c.length >= Math.max(80, N * 20) && contentWords >= Math.max(14, N * 3);

    // ── Design-only-no-files content density check ─────────────────────
    //
    // When the user picks Design-only AND uploads no files, the only signal
    // the LLM has for slide content is the prose they typed. Without that,
    // we end up generating filler text or hallucinating data, both of which
    // make decks that look polished but say nothing.
    //
    // Rule of thumb: ~600 chars / slide is roughly the minimum to cover a
    // typical bullet list + supporting datapoint (≈100 words/slide). Lower
    // than that and we're effectively guessing — at which point Research +
    // Design is the right tool.
    //
    // We surface this as a SEPARATE dialog kind so we can give bespoke
    // CTAs ("Add content" vs "Switch to Research + Design"), instead of
    // burying it in the generic warning list.
    const designOnlyNoFiles = !isResearch && !hasFile;
    const minCharsForDesign = (currentMode === "multi" ? N : 1) * 600;
    const tooThinForDesign = designOnlyNoFiles && c.length < minCharsForDesign;
    if (tooThinForDesign) {
      return {
        warnings: [],  // unused for thin-design-only — UI builds its own copy
        kind: "thin-design-only",
        meta: {
          chars: c.length,
          minChars: minCharsForDesign,
          slides: currentMode === "multi" ? N : 1,
        },
      };
    }

    // ── Apply matrix ───────────────────────────────────────────────────
    if (currentMode === "single" && !isResearch) {
      // (1) single design — pass if either path is satisfied
      if (!passSingleContent && !hasFile) {
        warnings.push({
          title: "Add content for this slide",
          hint: "A few bullets, a paragraph, or upload a source file.",
        });
      }
    } else if (currentMode === "single" && isResearch) {
      // (2) single research — need 1–2 lines of direction; files are optional
      if (!passGuidance) {
        warnings.push({
          title: "Describe what to research",
          hint: "1–2 lines on key questions or focus areas.",
        });
      }
    } else if (currentMode === "multi" && !isResearch) {
      // (3) multi design — full content OR (file + outline)
      if (!passMultiContent) {
        if (!hasFile) {
          warnings.push({
            title: `Add content for ${N} slides`,
            hint: "Or upload a file + a short outline / ToC.",
          });
        } else if (!passGuidance) {
          warnings.push({
            title: "Add a short outline",
            hint: "1–2 lines so the file is split sensibly across slides.",
          });
        }
      }
    } else {
      // (4) multi research — 1–2 lines of guidance / ToC
      if (!passGuidance) {
        warnings.push({
          title: "Add a short outline",
          hint: "1–2 lines so research splits into focused slides.",
        });
      }
    }

    return { warnings, kind: warnings.length ? "general" : null };
  };

  const validateAndProceed = (proceedFn) => {
    const result = checkSparseness(
      mode === "single" ? title : topic,
      contentText,
      mode,
      approach,
      attachedFiles.length,
      numSlides,
    );
    if (result.kind === null) {
      proceedFn();
      return;
    }
    setSparseDialog({
      open: true,
      warnings: result.warnings,
      kind: result.kind,
      meta: result.meta || null,
      onProceed: proceedFn,
    });
  };

  const handleGenerateSingle = async (approachOverride = null) => {
    const hasContent = !!contentText.trim();
    const hasTitle = !!title.trim();
    if (!hasTitle) { toast.error("Enter a title first"); return; }
    // Force user to pick an Approach before generation. The picker now
    // starts unselected (2026-02 feedback) so we must validate here.
    const effectiveApproach = approachOverride || approach;
    if (!effectiveApproach) { toast.error("Select an Approach above"); return; }
    // Content is REQUIRED for design approach (Opus has nothing to lay
    // out otherwise). For research approach it's OPTIONAL — the Wokelo
    // agent does its own information gathering, the textarea is just
    // for "Add instructions" (refinement hints). Without this branch,
    // hitting "Generate anyway" on the sparse-content dialog would fall
    // through here and silently fail with the toast, leaving the user
    // confused why nothing happened (2026-07-XX bug report).
    if (effectiveApproach !== "research" && !hasContent) {
      toast.error("Add content / layout first");
      return;
    }
    // Both Design and Research+Design now go through the same single async
    // POST + History redirect flow. For Research+Design the backend creates
    // the slide row immediately with status_detail="Researching..." then
    // chains research → slide generation server-side. No more interim
    // preview UI — matches deck path and the user's "all flows consistent"
    // requirement (2026-05-11).
    await runSlideGeneration(contentText.trim(), effectiveApproach);
  };

  const runResearchStep = async () => {
    // Legacy entry point — now identical to research-mode slide generation.
    // Retained so old callers (e.g. "switch to research" inside the thin-
    // content modal) keep working without modification.
    await runSlideGeneration(contentText.trim(), "research");
  };

  const runSlideGeneration = async (slideContent, approachOverride = null) => {
    setGenerating(true); setGenStatus("Generating slide..."); setElapsedTime(0);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsedTime(p => p + 1), 1000);
    const effectiveApproach = approachOverride || approach;
    try {
      const res = await axios.post(`${API}/generate`, {
        content: slideContent,
        title: title.trim(),
        raw_text: contentText.trim(),
        research_text: researchText,
        sources: researchSources,
        approach: effectiveApproach,
        // Research tier — only relevant when approach="research".
        // "public" → Wokelo Pro (free), "premium" → Wokelo Deep
        // research (+1 credit billed on success).
        research_model: effectiveApproach === "research" ? researchModel : "public",
        content_image_ids: attachedFiles.map(f => f.id),
        inspiration_file_ids: inspirationFiles.map(f => f.id),
        template_id: selectedTemplateId || undefined,
        engine: "advanced",
      }, { withCredentials: true });
      pollingRef.current = setInterval(() => pollSingle(res.data.id), 3000);
      window.dispatchEvent(new Event("credits:refresh"));
      window.dispatchEvent(new Event("history:refresh"));
    } catch (err) {
      setGenerating(false); clearInterval(timerRef.current);
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Failed to start generation";
      if (status === 402) {
        toast.error(detail);
        window.dispatchEvent(new CustomEvent("credits:open-upgrade"));
        // Re-throw so the kickOff wrapper marks this as failed and does NOT
        // navigate to /history — the user needs to stay on PPT Studio so
        // they can adjust slide count or close the upgrade modal and edit.
        throw err;
      } else {
        toast.error(detail);
      }
    }
  };

  // ═══ MULTI-SLIDE GENERATION ═══
  const pollDeck = useCallback(async (deckId) => {
    try {
      const res = await axios.get(`${API}/decks/${deckId}`);
      const d = res.data;
      setDeck(d); setGenStatus(d.status_detail || "Processing...");
      if (d.status === "complete") {
        clearInterval(deckPollingRef.current); clearInterval(timerRef.current);
        deckPollingRef.current = null; timerRef.current = null;
        setGenerating(false); toast.success("Deck generated!");
        fireFirstGenCelebrationIfPending("deck", d.id || deckId);
      } else if (d.status === "failed") {
        clearInterval(deckPollingRef.current); clearInterval(timerRef.current);
        deckPollingRef.current = null; timerRef.current = null;
        setGenerating(false);
        toast.info("Deck is queued due to heavy traffic — tap History to retry");
      }
    } catch {}
  }, []);

  const handleGenerateDeck = async (approachOverride = null) => {
    if (!topic.trim()) { toast.error("Enter a title"); return; }
    // `approachOverride` exists so the thin-content modal can switch the
    // user from "design" → "research" AND kick off generation in the same
    // click. Without it, React state batching means `approach` here would
    // still be the old value at the moment we POST, sending the wrong
    // generation strategy to the backend.
    const effectiveApproach = approachOverride || approach;
    if (!effectiveApproach) { toast.error("Select an Approach above"); return; }
    // Content is REQUIRED for design approach but OPTIONAL for research
    // — the Wokelo agent fetches its own content from the topic + any
    // instructions. Without this branch, the sparse-content "Generate
    // anyway" path silently failed here (2026-07-XX bug report).
    if (effectiveApproach !== "research" && !contentText.trim()) {
      toast.error("Add content / layout first");
      return;
    }
    setGenerating(true); setGenStatus("Starting..."); setElapsedTime(0);
    timerRef.current = setInterval(() => setElapsedTime(p => p + 1), 1000);

    try {
      // Server-chained: a single /generate-storyline call now creates the
      // placeholder deck row, charges credits, and runs storyline → deck
      // generation server-side. Frontend gets back the deck_id immediately
      // and navigates to History without waiting for storyline completion.
      setGenStatus("Queueing deck...");
      const storyRes = await axios.post(`${API}/generate-storyline`, {
        topic: topic.trim(),
        structure: "",
        num_slides: parseInt(numSlides),
        file_ids: attachedFiles.map(f => f.id),
        inspiration_file_ids: inspirationFiles.map(f => f.id),
        content_text: contentText.trim(),
        template_id: selectedTemplateId || undefined,
        engine: "advanced",
        // approach is forwarded so backend knows whether to fetch live
        // research (Wokelo Pro / Deep research) before building the
        // storyline.
        approach: effectiveApproach,
        // Research tier — only meaningful when approach="research".
        research_model: effectiveApproach === "research" ? researchModel : "public",
      }, { withCredentials: true });
      // Tell any open History tab to repaint immediately so the new "Queued"
      // card shows up without waiting for a 4s poll cycle.
      window.dispatchEvent(new Event("history:refresh"));
      window.dispatchEvent(new Event("credits:refresh"));
      // We don't poll the deck client-side anymore — History page handles that.
    } catch (err) {
      setGenerating(false); clearInterval(timerRef.current);
      const status = err.response?.status;
      const detail = err.response?.data?.detail || err.message || "Failed";
      if (status === 402) {
        toast.error(detail);
        window.dispatchEvent(new CustomEvent("credits:open-upgrade"));
        // Same as the single-slide path: re-throw so kickOffGeneration's
        // success flag flips to false → no navigation to /history.
        throw err;
      } else {
        toast.error(detail);
      }
    }
  };

  // ═══ HELPERS ═══
  // Wrap a Generate call with the "kicked off in background" UX:
  //   • show a friendly dialog confirming the job has started,
  //   • AWAIT the kick-off function so the backend has actually accepted the
  //     job (storyline → deck POST) before we navigate. Navigating mid-await
  //     unmounts the component and aborts the in-flight chain, which silently
  //     loses the deck (P0 bug). Therefore the navigate is gated on the
  //     handler resolving.
  //   • dialog stays visible for ~3s minimum for visual feedback even if the
  //     handler resolves faster.
  const kickOffGeneration = async (fn) => {
    // Pre-fetch the ETA before opening the dialog so the "Usually takes ~Xm"
    // line is present from frame 1. We don't block on it though — if the
    // call is slow the dialog opens with the spinner and the line drops in
    // when the response lands. Errors are swallowed (rare; falls back to
    // the spinner-only dialog).
    setGenEtaSeconds(null);
    const slidesN = mode === "multi" ? Math.max(1, parseInt(numSlides, 10) || 1) : 1;
    axios
      .get(`${API}/generation-eta`, {
        params: {
          kind: mode === "multi" ? "deck" : "single",
          approach,
          num_slides: slidesN,
          // research_model only meaningful when approach="research".
          // Backend ignores it for design-only runs.
          research_model: approach === "research" ? researchModel : "public",
        },
        withCredentials: true,
      })
      .then(r => setGenEtaSeconds(r.data?.estimate_seconds || null))
      .catch(() => {});

    setGenStartedOpen(true);
    const minDisplay = new Promise(r => setTimeout(r, 3000));
    let succeeded = true;
    try {
      // Wait for the handler to fire its critical API calls. For decks this
      // is storyline-poll → /generate-deck POST. For singles it's /generate.
      // The handler internally also starts a polling interval, but we don't
      // wait on the polling — once the deck is queued server-side, the
      // backend will keep processing even after we navigate away.
      await fn();
    } catch {
      succeeded = false;
    }
    if (succeeded) {
      // Only the 3s heads-up makes sense on success; on failure we close
      // immediately so the upgrade modal / error toast aren't covered.
      await minDisplay;
    }
    setGenStartedOpen(false);
    if (succeeded) navigate("/history");
  };

  const handleDownload = (id, name = "slide.pptx") => {
    const link = document.createElement("a");
    link.href = `${API}/download/${id}`;
    link.download = name;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  const handleDownloadDeck = () => {
    if (!deck?.id) return;
    const link = document.createElement("a");
    link.href = `${API}/download-deck/${deck.id}`;
    link.download = "deck.pptx";
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  const handleNew = () => {
    setResult(null); setDeck(null); setContentText(""); setAttachedFiles([]);
    setInspirationFiles([]); setTopic(""); setTitle(""); setElapsedTime(0); setCurrentSlide(0);
    setGenerating(false); setGenStatus("");
  };

  const formatTime = (s) => { const m = Math.floor(s / 60); return m > 0 ? `${m}m ${s % 60}s` : `${s}s`; };

  // Friendly "~Xm" / "~Xm Ys" rendering used in the kick-off dialog. Rounds
  // sub-minute values up to 1m so we don't show "~30s" — anything that
  // small reads as instant on the wire and confuses the user.
  const formatEta = (s) => {
    if (!s || s <= 0) return "1m";
    if (s < 90) return "~1m";
    const m = Math.round(s / 60);
    return `${m}m`;
  };

  const slides = deck?.slides || [];
  const completedSlides = slides.filter(s => s.status === "complete");
  // slide_thumbnails and thumbnail_url hold item-IDs; resolve via /api/thumbnails/{id}.
  // Supports legacy values that were full URLs or data: URIs.
  const resolveThumb = (val) => {
    if (!val) return null;
    if (val.startsWith("http") || val.startsWith("data:")) return val;
    // strip any leading slashes / file prefixes; keep only the id
    const id = val.replace(/^.*[\\/]/, "").replace(/\.png$/i, "");
    return `${API}/thumbnails/${id}`;
  };
  const slideThumbIds = deck?.slide_thumbnails?.length
    ? deck.slide_thumbnails
    : completedSlides.map(s => s.thumbnail_url || s.id).filter(Boolean);
  const slideThumbnails = slideThumbIds.map(resolveThumb).filter(Boolean);
  const showSingleResult = mode === "single" && result;
  const showDeckResult = mode === "multi" && deck?.status === "complete" && slideThumbnails.length > 0;

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════
  return (
    <div className="page-container bg-[#FAFAF7] min-h-screen -mx-4 -mt-4 px-4 pt-4 pb-12 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8" data-testid="ppt-generator-page">
      {/* Page background sampled directly from the supplied HTML mock
          (rgb(250, 250, 247) → #FAFAF7) — a barely-warm off-white,
          NOT the heavier cream `#FAF6EE` we tried initially. The mock
          deliberately keeps the canvas very light so the white cards
          feel like floating panels rather than blending in. */}
      {/* First-time-user coachmark tour. Renders nothing for users whose
          `onboarding_status` is anything other than `pending`. */}
      <CoachmarkTour active={true} />
      {/* Celebration modal — listens for `onboarding:first-complete` and
          fires once after the user's first successful generation. */}
      <FirstDeckCelebration />
      <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
        <span>Home</span><span className="text-gray-300">/</span><span className="text-gray-900">PPT Studio</span>
      </div>

      {/* ═══ INPUT FORM (4-step layout: Approach → Name+Size → Content → Look) ═══
          Redesign reference: user-supplied mocks (2026-06-30). Order
          matters: the approach choice gates the content textarea label
          ("Add your content" vs "Anything we should focus on?") and
          whether the file-upload zone is visible at all — Wokelo
          research mode hides uploads since the agent does its own
          information-gathering pass. Section numbering (1–4) is
          deliberately surfaced in the UI so the bottom-of-page
          progress hint ("Choose an option in step 1") can refer back
          to specific steps. */}
      {!showSingleResult && !showDeckResult && !generating && (
        <div className="space-y-6">
          {/* ── Page header ─────────────────────────────────────────── */}
          <div className="space-y-1.5" data-testid="create-deck-header">
            <h1 className="text-2xl font-bold text-gray-900">Create a deck</h1>
          </div>

          {/* ── 1. Where should the content come from? ──────────────── */}
          <Card className="border-gray-200 shadow-none" data-testid="approach-card">
            <CardContent className="pt-5 pb-5 space-y-4">
              <SectionHeader
                step={1}
                title="Where should the content come from?"
              />
              <ApproachAndModel approach={approach} setApproach={setApproach} researchModel={researchModel} setResearchModel={setResearchModel} />
            </CardContent>
          </Card>

          {/* ── 2. Name it and pick a size ──────────────────────────── */}
          <Card className="border-gray-200 shadow-none" data-testid="title-slider-card">
            <CardContent className="pt-5 pb-5 space-y-5">
              <SectionHeader step={2} title="Name it and pick a size" />
              <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
                <div className="flex-1 min-w-0 sm:basis-[60%]">
                  <p className="text-xs font-semibold text-gray-700 mb-2">Deck title</p>
                  <input
                    type="text"
                    value={unifiedTitle}
                    onChange={e => setUnifiedTitle(e.target.value)}
                    placeholder={mode === "single"
                      ? "e.g., Market Overview"
                      : "e.g., Cloud Computing Q4 2026"}
                    className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-300"
                    data-testid="title-input"
                  />
                </div>

                <div className="sm:basis-[40%] sm:min-w-[300px]">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-gray-700">How many slides?</p>

                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setNumSlides(String(Math.max(1, slidesInt - 1)))}
                        disabled={slidesInt <= 1}
                        aria-label="Decrease slides"
                        data-testid="slide-count-decrement"
                        className="flex items-center justify-center w-6 h-6 rounded-md border border-gray-200 bg-white text-gray-500 hover:border-gray-400 hover:text-black disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <div className="flex items-baseline gap-1">
                        <input
                          type="number"
                          min={1}
                          max={20}
                          step={1}
                          value={slidesInt}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === "") { setNumSlides(""); return; }
                            const n = parseInt(v, 10);
                            if (!Number.isNaN(n)) setNumSlides(String(n));
                          }}
                          onBlur={(e) => {
                            const n = parseInt(e.target.value, 10);
                            const clamped = Math.max(1, Math.min(20, Number.isNaN(n) ? 1 : n));
                            setNumSlides(String(clamped));
                          }}
                          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                          aria-label="Number of slides"
                          data-testid="slide-count-input"
                          className="w-10 text-center text-lg font-semibold text-gray-900 tabular-nums bg-transparent border-0 border-b border-transparent hover:border-gray-200 focus:border-gray-400 focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        />
                        <span className="text-sm font-medium text-gray-500" data-testid="slide-count-label">
                          {slidesInt === 1 ? "slide" : "slides"}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setNumSlides(String(Math.min(20, slidesInt + 1)))}
                        disabled={slidesInt >= 20}
                        aria-label="Increase slides"
                        data-testid="slide-count-increment"
                        className="flex items-center justify-center w-6 h-6 rounded-md border border-gray-200 bg-white text-gray-500 hover:border-gray-400 hover:text-black disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                      {isTrial && slidesInt > maxDeckSlides && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                          <Lock className="w-2.5 h-2.5" />
                          Trial caps at {maxDeckSlides}
                        </span>
                      )}
                    </div>
                  </div>

                  <Slider
                    min={1}
                    max={20}
                    step={1}
                    value={[slidesInt]}
                    onValueChange={(v) => setNumSlides(String(v[0]))}
                    className="w-full"
                    data-testid="slide-count-slider"
                  />
                  <div className="relative h-4 mt-1.5">
                    {[1, 5, 10, 15, 20].map(tick => {
                      const pct = ((tick - 1) / 19) * 100;
                      return (
                        <span
                          key={tick}
                          className="absolute text-[10px] text-gray-400 tabular-nums"
                          style={{ left: `${pct}%`, transform: "translateX(-50%)" }}
                        >
                          {tick}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── 3. Add your content / Anything we should focus on? ─────
              Label + placeholder + presence of upload zone are all
              driven by `approach`. Research mode deliberately HIDES the
              upload zone — the Wokelo agent does its own information
              gathering, so a user-attached file would conflict with
              the storyline produced from the agent's findings. */}
          <Card className="border-gray-200 shadow-none" data-testid="add-content-card">
            <CardContent className="pt-5 pb-5 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <SectionHeader
                  step={3}
                  title={approach === "research"
                    ? "Add instructions"
                    : "Add your content"}
                />
                <div className="flex items-center gap-2 mt-0.5">
                  <SavedPromptsPicker
                    value={contentText}
                    onChange={setContentText}
                    testId="content-text"
                    prompts={savedPrompts}
                    onPromptsChange={refreshPrompts}
                  />
                  {(contentText || attachedFiles.length > 0) && (
                    <button onClick={() => { setContentText(""); setAttachedFiles([]); }} className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors" data-testid="clear-content-btn">
                      <Trash2 className="w-3 h-3" />Clear
                    </button>
                  )}
                </div>
              </div>
              <div className="relative">
                <Textarea
                  ref={contentTextRef}
                  value={contentText}
                  onChange={e => setContentText(e.target.value)}
                  placeholder={approach === "research"
                    ? "e.g. Focus on competitive positioning and last 3 years of funding rounds."
                    : "Paste your text, bullet points, or numbers — anything Wokelo should turn into slides."}
                  className="min-h-[140px] border-gray-200 text-sm pr-12 pb-12 bg-white"
                  data-testid="content-text-input"
                />
                <div className="absolute bottom-2 right-2">
                  <VoiceMicButton
                    value={contentText}
                    onChange={setContentText}
                    inputRef={contentTextRef}
                    testId="content-text"
                  />
                </div>
              </div>
              <span className="text-xs text-gray-400 block">{contentText.length} chars{attachedFiles.length > 0 ? ` · ${attachedFiles.length} file(s)` : ""}</span>

              {/* File uploads — DESIGN approach only.
                  Research mode hides this whole section (per
                  2026-06-30 spec) because the Wokelo agent does its
                  own information gathering and a user-attached file
                  here would muddy that pipeline. */}
              {approach !== "research" && (
                <>
                  {attachedFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {attachedFiles.map(f => (
                        <div key={f.id} className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-600">
                          <FileText className="w-3.5 h-3.5 text-gray-400" />
                          <span className="max-w-[120px] truncate">{f.name}</span>
                          <button onClick={() => { setAttachedFiles(prev => prev.filter(x => x.id !== f.id)); setUploadError(""); }} className="ml-1 p-0.5 hover:bg-gray-200 rounded-full">
                            <X className="w-3 h-3 text-gray-400" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="pt-1">
                    <p className="text-xs font-semibold text-gray-700 mb-2">Upload files (optional)</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept={allowedFileTypes.join(",")}
                      onChange={handleFileAttach}
                      className="hidden"
                    />
                    <div
                      onClick={() => { if (attachedFiles.length < maxContentUploads) fileInputRef.current?.click(); }}
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => { e.preventDefault(); if (e.dataTransfer.files?.length && attachedFiles.length < maxContentUploads) { const dt = { target: { files: e.dataTransfer.files } }; handleFileAttach(dt); } }}
                      className={`flex items-center justify-center gap-2 px-3 py-3 min-h-[68px] border-2 border-dashed rounded-lg transition-all ${uploadError ? "border-red-300 bg-red-50/50" : attachedFiles.length >= maxContentUploads ? "border-gray-100 text-gray-300 cursor-not-allowed" : "border-gray-200 hover:border-gray-300 cursor-pointer bg-[#FAF8F1]/40"}`}
                      data-testid="attach-drop-strip"
                    >
                      <Upload className={`w-3.5 h-3.5 flex-shrink-0 ${uploadError ? "text-red-500" : ""}`} />
                      <span className={`text-xs leading-snug text-center ${uploadError ? "text-red-700" : ""}`}>
                        {attachedFiles.length >= maxContentUploads ? (
                          `Max ${maxContentUploads} file(s) on ${accountType} plan`
                        ) : (
                          <>
                            Drag &amp; drop or click to attach files
                            <span className="block text-[11px] text-gray-400 mt-0.5">
                              (.pdf, .docx, .xlsx, .pptx, .jpeg, .png)
                            </span>
                          </>
                        )}
                      </span>
                    </div>
                  </div>

                  {uploadError && (
                    <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-xs"
                         role="alert" data-testid="upload-error-inline">
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                      <span className="flex-1">{uploadError}</span>
                      <button onClick={() => setUploadError("")} className="text-red-400 hover:text-red-700 leading-none" aria-label="Dismiss">×</button>
                    </div>
                  )}
                  {pendingUploads.filter(p => p.kind === "raw_data").length > 0 && (
                    <div className="mt-2 space-y-1.5" data-testid="upload-progress">
                      {pendingUploads.filter(p => p.kind === "raw_data").map(p => (
                        <div key={p.key} className="flex items-center gap-2 px-3 py-2 rounded-md bg-gray-50 border border-gray-200 text-xs text-gray-700">
                          <span className="relative flex h-2 w-2 flex-shrink-0">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
                          </span>
                          <span className="flex-1 truncate font-medium">{p.name}</span>
                          <span className="text-gray-400 flex-shrink-0">{(p.size / 1024 / 1024).toFixed(1)} MB · uploading…</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {isTrial && (
                    <p className="text-[11px] text-gray-500 mt-2" data-testid="trial-upsell">
                      <span className="font-semibold">Trial plan:</span> 1 upload, PDFs only, max 10&nbsp;MB, 50 pages.
                      Upgrade to <span className="font-semibold">Starter</span> for all file formats and higher upload limits.
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* ── 4. Pick a look ───────────────────────────────────────── */}
          <Card className="border-gray-200 shadow-none" data-testid="pick-a-look-card">
            <CardContent className="pt-5 pb-5 space-y-4">
              <SectionHeader step={4} title="Pick a look" />
              <div className={`grid grid-cols-1 ${mode === "single" ? "sm:grid-cols-2" : ""} gap-5`}>
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-2">Slide design</p>
                  {templatesLoading ? (
                    <div className="h-10 flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin" />Loading...</div>
                  ) : (
                    <div className="space-y-1.5">
                      <Select value={selectedTemplateId} onValueChange={(v) => { if (v === "custom") return; setSelectedTemplateId(v); }}>
                        <SelectTrigger className="w-full" data-testid="template-select-trigger">
                          <SelectValue placeholder="Select a template" />
                        </SelectTrigger>
                        <SelectContent>
                          {visibleTemplates.map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.name}{t.is_default ? " (Default)" : ""}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Link
                        to="/personalize"
                        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                        data-testid="request-custom-template"
                      >
                        <ExternalLink className="w-3 h-3" />Request Custom Template
                      </Link>
                    </div>
                  )}
                </div>

                {mode === "single" && (
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-2">Match a style <span className="font-normal text-gray-400">(.jpg, .png — up to {MAX_STYLE_IMAGES})</span></p>
                  <input ref={inspirationFileRef} type="file" multiple accept="image/jpeg,image/png" onChange={handleStyleUpload} className="hidden" />
                  {inspirationFiles.length === 0 ? (
                    <div
                      onDragOver={e => { e.preventDefault(); setIsDraggingStyle(true); }}
                      onDragLeave={() => setIsDraggingStyle(false)}
                      onDrop={handleStyleDrop}
                      onClick={() => inspirationFileRef.current?.click()}
                      className={`flex items-center justify-center gap-2 h-16 border-2 border-dashed rounded-lg cursor-pointer transition-all ${isDraggingStyle ? "border-black bg-gray-100" : "border-gray-200 hover:border-gray-300 bg-[#FAF8F1]/40"}`}
                      data-testid="style-ref-upload">
                      <Upload className={`w-4 h-4 ${isDraggingStyle ? "text-black" : "text-gray-400"}`} />
                      <p className="text-xs text-gray-500">{isDraggingStyle ? "Drop here" : "Drag and drop or click to upload images (.jpg, .png)"}</p>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <div className={`flex items-center gap-2 p-1.5 rounded-lg transition-all ${isDraggingStyle ? "bg-gray-100 ring-2 ring-black ring-dashed" : ""}`}
                        onDragOver={e => { e.preventDefault(); setIsDraggingStyle(true); }}
                        onDragLeave={() => setIsDraggingStyle(false)}
                        onDrop={handleStyleDrop}>
                        {inspirationFiles.map(f => (
                          <div key={f.id} className="relative group">
                            <div className="w-16 h-10 bg-gray-100 rounded overflow-hidden">
                              {f.thumbnail_url ? <img src={f.thumbnail_url} alt={f.name} className="w-full h-full object-cover" /> : <Image className="w-4 h-4 text-gray-400 m-auto" />}
                            </div>
                            <button onClick={() => setInspirationFiles(prev => prev.filter(x => x.id !== f.id))} className="absolute -top-1 -right-1 p-0.5 bg-black/60 hover:bg-black rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-2.5 h-2.5" /></button>
                          </div>
                        ))}
                        {inspirationFiles.length < MAX_STYLE_IMAGES && (
                          <button onClick={() => inspirationFileRef.current?.click()} className="flex items-center justify-center w-16 h-10 border-2 border-dashed border-gray-200 hover:border-gray-300 rounded text-gray-400 hover:text-gray-600 transition-colors">
                            <Upload className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {pendingUploads.filter(p => p.kind === "inspiration").map(p => (
                          <div key={p.key} className="w-16 h-10 rounded border border-gray-200 bg-gray-50 flex items-center justify-center" title={`${p.name} · ${(p.size/1024/1024).toFixed(1)} MB · uploading…`}>
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Footer: step-progress hint + Generate.
              The hint changes to call out the FIRST missing piece so
              the user knows exactly what to do next instead of staring
              at a disabled button wondering why. */}
          <div className="flex items-center justify-between gap-4 pt-1">
            <p className="text-xs text-gray-500 flex-1" data-testid="generate-progress-hint">
              {!approach
                ? "Choose how the content should come from to begin."
                : !unifiedTitle.trim()
                  ? "Give your deck a name to continue."
                  : (approach === "design" && !contentText.trim() && attachedFiles.length === 0)
                    ? "Add some content to continue."
                    : "Ready when you are."}
            </p>
            <Button
              onClick={() => validateAndProceed(() => {
                kickOffGeneration(mode === "single" ? handleGenerateSingle : handleGenerateDeck);
              })}
              disabled={
                generating ||
                !approach ||
                (mode === "single" ? !title.trim() : !topic.trim()) ||
                (approach === "design" && !contentText.trim() && attachedFiles.length === 0)
              }
              className="bg-black hover:bg-gray-800 h-11 px-6 text-sm font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
              data-testid="generate-btn">
              Generate deck
            </Button>
          </div>
        </div>
      )}

      {/* ═══ GENERATING STATE ═══ */}
      {generating && (
        <div className="max-w-xl mx-auto text-center py-16 space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-gray-500 mx-auto" />
          <p className="text-sm font-medium text-gray-700">{genStatus}</p>
          <div className="flex items-center justify-center gap-1 text-xs text-gray-400">
            <Clock className="w-3 h-3" />
            <span>{formatTime(elapsedTime)} elapsed</span>
          </div>
          {mode === "multi" && slides.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-6">
              {slides.map((s, i) => (
                <div key={s.id} className={`p-2 rounded border text-xs text-center ${s.status === "complete" ? "border-green-200 bg-green-50 text-green-700" : s.status === "failed" ? "border-red-200 bg-red-50 text-red-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
                  Slide {i + 1}: {s.status === "complete" ? "Done" : s.status === "failed" ? "Failed" : "..."}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ SINGLE SLIDE RESULT ═══ */}
      {showSingleResult && (
        <div className="flex gap-5 animate-slide-in" data-testid="result-view">
          <div className="flex-1 min-w-0">
            {(() => {
              const thumb = resolveThumb(result.thumbnail_url || result.id);
              return thumb ? (
                <div className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                  <img src={thumb} alt="Slide preview" className="w-full" data-testid="single-slide-preview" />
                </div>
              ) : (
                <div className="rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center" style={{ minHeight: 400 }}>
                  <p className="text-gray-400 text-sm">Preview not available</p>
                </div>
              );
            })()}
            <div className="flex gap-2 mt-3">
              <Button onClick={() => handleDownload(result.id)} className="flex-1 bg-black hover:bg-gray-800 h-10" data-testid="download-btn">
                <Download className="w-4 h-4" />Download PPTX
              </Button>
              {result.id && (
                <Link to={`/details/slide/${result.id}`}>
                  <Button variant="outline" className="h-10"><Eye className="w-4 h-4" /></Button>
                </Link>
              )}
            </div>
          </div>
          <div className="w-72 flex-shrink-0 space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm font-semibold text-green-800">Slide Generated!</p>
            </div>
            <Button variant="outline" onClick={handleNew} className="w-full h-9 text-xs">Start New Slide</Button>
          </div>
        </div>
      )}

      {/* ═══ DECK RESULT ═══ */}
      {showDeckResult && (
        <div className="space-y-4 animate-slide-in">
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
            <p className="text-sm font-semibold text-green-800">Deck Generated! ({slideThumbnails.length} slides)</p>
            <Button variant="outline" size="sm" onClick={handleNew}>Start New Deck</Button>
          </div>

          <div className="bg-gray-100 rounded-xl p-4 flex items-center justify-center" style={{ minHeight: 400 }}>
            {slideThumbnails[currentSlide] ? (
              <img src={slideThumbnails[currentSlide]} alt={`Slide ${currentSlide + 1}`} className="max-h-[480px] w-auto rounded shadow-lg" />
            ) : (
              <p className="text-gray-400 text-sm">No preview</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={() => setCurrentSlide(p => Math.max(0, p - 1))} disabled={currentSlide === 0}>
              <ChevronLeft className="w-4 h-4" />Prev
            </Button>
            <span className="text-sm text-gray-500">Slide {currentSlide + 1} of {slideThumbnails.length}</span>
            <Button variant="outline" size="sm" onClick={() => setCurrentSlide(p => Math.min(slideThumbnails.length - 1, p + 1))} disabled={currentSlide >= slideThumbnails.length - 1}>
              Next<ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex gap-2 overflow-x-auto pb-2">
            {slideThumbnails.map((thumb, i) => (
              <button key={i} onClick={() => setCurrentSlide(i)} className={`flex-shrink-0 w-24 aspect-video rounded overflow-hidden border-2 transition-colors ${currentSlide === i ? "border-black" : "border-transparent hover:border-gray-300"}`}>
                <img src={thumb} alt={`Slide ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>

          <Button onClick={handleDownloadDeck} className="w-full bg-black hover:bg-gray-800 h-12" data-testid="download-deck-btn">
            <Download className="w-4 h-4" />Download Full Deck ({slideThumbnails.length} slides)
          </Button>
        </div>
      )}

      {/* Sparseness warning dialog — variant depends on `sparseDialog.kind`.
          - "thin-design-only": user picked Design-only with no files and the
            content is below the per-slide minimum. Two CTAs steer them to
            either paste content or switch to Research+Design.
          - "general": every other validation miss → generic warnings list
            with Edit-details / Generate-anyway. */}
      <Dialog
        open={sparseDialog.open}
        onOpenChange={(o) => setSparseDialog(s => ({ ...s, open: o }))}
      >
        <DialogContent data-testid="sparse-warning-dialog">
          {sparseDialog.kind === "thin-design-only" ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  Inputs look thin
                </DialogTitle>
                <DialogDescription className="pt-1 text-sm text-gray-500">
                  You picked <span className="font-medium text-gray-700">Use only my content</span>. Choose one option to continue.
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-2.5 pt-2">
                {/* Option 1 — paste/upload */}
                <button
                  type="button"
                  data-testid="sparse-add-content-btn"
                  onClick={() => {
                    setSparseDialog({ open: false, warnings: [], onProceed: null, kind: null, meta: null });
                    const el = document.querySelector('[data-testid="content-text-input"]');
                    if (el) {
                      el.focus();
                      el.scrollIntoView({ behavior: "smooth", block: "center" });
                    }
                  }}
                  className="group flex items-center gap-3 p-3.5 border border-gray-200 rounded-lg hover:border-gray-900 hover:bg-gray-50 transition-all text-left"
                >
                  <div className="flex-shrink-0 w-9 h-9 rounded-md bg-gray-100 group-hover:bg-gray-900 group-hover:text-white text-gray-600 flex items-center justify-center transition-colors">
                    <Pencil className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">Paste text or upload files</p>
                    <p className="text-xs text-gray-500 mt-0.5">Provide your own content or source files.</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-700 transition-colors" />
                </button>

                {/* Option 2 — switch to research */}
                <button
                  type="button"
                  data-testid="sparse-switch-research-btn"
                  onClick={() => {
                    setApproach("research");
                    setSparseDialog({ open: false, warnings: [], onProceed: null, kind: null, meta: null });
                    // CRITICAL: also kick off generation in research mode.
                    // Previously this just flipped approach + showed a toast,
                    // leaving the user stuck on PPT Studio wondering where
                    // their deck went. Pass "research" explicitly so the
                    // handler doesn't depend on React state having committed.
                    if (mode === "single") {
                      // Single + research now uses the same kickOff flow.
                      // Pass "research" explicitly so React state batching
                      // doesn't trip us up.
                      kickOffGeneration(() => handleGenerateSingle("research"));
                    } else {
                      kickOffGeneration(() => handleGenerateDeck("research"));
                    }
                  }}
                  className="group flex items-center gap-3 p-3.5 border border-gray-200 rounded-lg hover:border-teal-600 hover:bg-teal-50/40 transition-all text-left"
                >
                  <div className="flex-shrink-0 w-9 h-9 rounded-md bg-teal-50 text-teal-600 group-hover:bg-teal-600 group-hover:text-white flex items-center justify-center transition-colors">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">Switch to Wokelo's research agent</p>
                    <p className="text-xs text-gray-500 mt-0.5">Wokelo researches and writes the content for you.</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-teal-600 transition-colors" />
                </button>
              </div>

              {sparseDialog.meta && (
                <p className="text-[11px] text-gray-400 pt-3 mt-1 border-t border-gray-100">
                  Need ~<span className="font-medium text-gray-600">{sparseDialog.meta.minChars.toLocaleString()} chars</span>
                  {sparseDialog.meta.slides > 1 ? ` for ${sparseDialog.meta.slides} slides` : " for 1 slide"} ·
                  you have <span className="font-medium text-gray-600">{sparseDialog.meta.chars.toLocaleString()}</span>.
                </p>
              )}
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  Inputs look thin
                </DialogTitle>
                <DialogDescription asChild>
                  <div className="space-y-3 pt-1 text-sm">
                    <p className="text-gray-700">More detail = better output. Quick fixes:</p>
                    <ul className="space-y-2.5">
                      {sparseDialog.warnings.map((w, i) => (
                        <li key={i} data-testid={`sparse-warning-${i}`} className="flex gap-2.5">
                          <span className="text-teal-600 font-bold mt-0.5">•</span>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 leading-snug">{w.title || w}</p>
                            {w.hint && (
                              <p className="text-xs text-gray-500 mt-0.5 leading-snug">{w.hint}</p>
                            )}
                            {w.example && (
                              <p className="text-xs text-gray-400 mt-0.5 italic leading-snug">e.g. "{w.example}"</p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  data-testid="sparse-edit-btn"
                  onClick={() => setSparseDialog(s => ({ ...s, open: false }))}
                >
                  Edit details
                </Button>
                <Button
                  data-testid="sparse-proceed-btn"
                  className="bg-black hover:bg-gray-800"
                  onClick={() => {
                    const fn = sparseDialog.onProceed;
                    setSparseDialog({ open: false, warnings: [], onProceed: null, kind: null, meta: null });
                    if (fn) fn();
                  }}
                >
                  Generate anyway
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Generation kicked off — friendly 3s heads-up before History redirect */}
      <Dialog open={genStartedOpen} onOpenChange={setGenStartedOpen}>
        <DialogContent className="max-w-md" data-testid="gen-started-dialog">
          <DialogHeader>
            <div className="mx-auto w-14 h-14 rounded-full bg-teal-50 flex items-center justify-center mb-2">
              <Loader2 className="w-6 h-6 text-teal-600 animate-spin" />
            </div>
            <DialogTitle className="text-center text-xl font-bold">
              Preparing your {mode === "single" ? "slide" : "deck"}…
            </DialogTitle>
            <DialogDescription className="text-center pt-1">
              Hang tight for a moment — once it's queued, we'll take you to History where you can watch progress and download the moment it's done.
            </DialogDescription>
          </DialogHeader>
          {genEtaSeconds ? (
            <div className="flex items-center justify-center gap-1.5 text-xs text-gray-500 -mt-1" data-testid="gen-started-eta">
              <Clock className="w-3 h-3" />
              <span>Usually takes ~{formatEta(genEtaSeconds)}</span>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// ApproachAndModel — two-card Approach picker + inline Research
// model picker (visible only when Research+Design is selected).
// Lives inside the same card as Title so the user has a single
// "what / how" decision area before they describe content.
// ─────────────────────────────────────────────────────────────
function ApproachAndModel({ approach, setApproach, researchModel, setResearchModel }) {
  // Two-card chooser. Cards are vertically compact — the time-estimate
  // sits absolutely positioned at bottom-right (matches the supplied
  // HTML mock) and the icon + title sit on a single horizontal row.
  const nothingSelected = !approach;
  const cards = [
    {
      id: "design",
      icon: <Pencil className="w-5 h-5" />,
      title: "I'll add my own content",
      sub: "~ 2-3 min / slide",
    },
    {
      id: "research",
      icon: <Sparkles className="w-5 h-5" />,
      title: "Let Wokelo do the research",
      sub: "+ 15-20 min",
    },
  ];

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {cards.map(c => {
          const selected = approach === c.id;
          const borderClass = selected
            ? "border-gray-900 bg-white shadow-sm"
            : nothingSelected
              ? "border-dashed border-gray-300 bg-white hover:border-gray-400"
              : "border-gray-200 bg-white hover:border-gray-300";
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setApproach(c.id)}
              data-testid={`approach-${c.id}`}
              aria-pressed={selected}
              className={`relative text-left rounded-xl border px-4 py-3.5 transition-all min-h-[78px] ${borderClass}`}
            >
              {selected ? (
                <span
                  className="absolute top-3 right-3 w-5 h-5 rounded-full bg-gray-900 text-white flex items-center justify-center"
                  data-testid={`approach-${c.id}-selected`}
                >
                  <Check className="w-3 h-3" strokeWidth={3} />
                </span>
              ) : nothingSelected ? (
                <span
                  className="absolute top-3 right-3 w-5 h-5 rounded-full border-2 border-gray-300 bg-white"
                  data-testid={`approach-${c.id}-radio-empty`}
                  aria-hidden="true"
                />
              ) : null}
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center ${
                  selected ? "bg-gray-900 text-white" : "bg-[#F2EFE7] text-gray-500"
                }`}>
                  {c.icon}
                </div>
                <div className="min-w-0">
                  <p className="text-base font-semibold text-gray-900 leading-tight">{c.title}</p>
                </div>
              </div>
              {/* Time aspect absolutely positioned at bottom-right so it
                  doesn't add to the card's vertical footprint (matches
                  the supplied HTML mock). */}
              {c.sub ? (
                <span
                  className="absolute bottom-2.5 right-3 text-[11px] font-semibold text-gray-400 whitespace-nowrap"
                  data-testid={`approach-${c.id}-time`}
                >
                  {c.sub}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Research-model picker REMOVED 2026-06-30 follow-up.
          Wokelo Pro ("Quick analysis") is now the only path — we
          hard-default `researchModel` to "public" at component mount
          so any backend logic that branches on it keeps working
          without code changes downstream. Users no longer see the
          Public/Premium radios. */}
    </div>
  );
}



// ── Helpers used by the 4-step PPT Studio layout ──────────────────────

/**
 * Numbered step header used by each of the 4 cards.
 *
 * Renders a circular dark step pip ("1") next to the section title.
 * The optional `right` slot lets the parent stuff extra buttons (Save /
 * See examples / etc.) into the same row without duplicating the
 * heading markup at every callsite.
 */
function SectionHeader({ step, title, right = null }) {
  // Section titles bumped from text-base → text-lg per 2026-06-30
  // follow-up so they sit more prominently on top of the card. We
  // keep the prop name `step` for backwards-compat with calling sites,
  // even though the pip itself was retired in the previous pass.
  return (
    <div className="flex items-center justify-between gap-3">
      <h2 className="text-lg font-semibold text-gray-900" data-testid={`section-header-${step}`}>{title}</h2>
      {right}
    </div>
  );
}

/**
 * "See examples" popover REMOVED 2026-06-30 follow-up.
 * Kept the import of `Sparkles` in `lucide-react` because it's still
 * used by the Approach card icon.
 */
